import React, { StrictMode, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, Navigate, RouterProvider, useLocation, useParams } from "react-router-dom";
import "./index.css";
import Tactics from "./routes/Tactics";
import { RecoilRoot } from "recoil";
import { migrateStoredLanguage, startLanguageSync } from "./v2/i18n/documentLanguage";
import "./i18n";

// The live viewer (formerly /v2) is a separate, lazily loaded chunk.
const LiveViewerPage = React.lazy(() => import("./v2/pages/LiveViewerPage"));

const liveViewer = (sponsor: boolean) => (
  <Suspense fallback={null}>
    <LiveViewerPage sponsor={sponsor} />
  </Suspense>
);

/**
 * The /v2 addresses were shared while sales tested the new viewer; they keep
 * working and land on the same page at its real address, query and hash kept
 * (`?theme=light`, `?mock=1`).
 */
function FormerV2Address({ sponsor }: { sponsor: boolean }): JSX.Element {
  const { id = "" } = useParams();
  const { search, hash } = useLocation();
  const path = `${sponsor ? "/sponsor" : ""}/shorts/${encodeURIComponent(id)}`;
  return <Navigate to={`${path}${search}${hash}`} replace />;
}

const router = createBrowserRouter([
  {
    path: "/shorts/:id",
    element: liveViewer(false),
  },
  {
    path: "/sponsor/shorts/:id",
    element: liveViewer(true),
  },
  // The previous viewer stays reachable while anyone still needs it.
  {
    path: "/legacy/shorts/:id",
    element: <Tactics />,
  },
  {
    path: "/legacy/sponsor/shorts/:id",
    element: <Tactics />,
  },
  {
    path: "/v2/shorts/:id",
    element: <FormerV2Address sponsor={false} />,
  },
  {
    path: "/v2/sponsor/shorts/:id",
    element: <FormerV2Address sponsor={true} />,
  },
]);

/**
 * Language, before the first paint:
 *   · a stored code that matches no folder (`sp`, `en-US`, nothing at all) is
 *     rewritten to one that does — `sp` is why Spanish never loaded;
 *   · `<html lang>` follows the active language from here on, which is what a
 *     screen reader and the `:lang(zh)` rules in `v2.css` read.
 */
migrateStoredLanguage();
startLanguageSync();

if (!localStorage.theme) {
  localStorage.theme = "light";
  document.documentElement.classList.add("light");
}
if (localStorage.theme === "dark") {
  document.documentElement.classList.add("dark");
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(
  <StrictMode>
    <RecoilRoot>
      <RouterProvider router={router} />
    </RecoilRoot>
  </StrictMode>
);
